import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { pluralize } from '@/utils/format';
import type { Domain } from '@/types/domain';

export interface GenerateConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Domains assigned to at least one node */
  domains: Domain[];
  onGenerate: (domainId: string) => Promise<unknown>;
}

/**
 * Generate an nginx config for a domain.
 * Mount only while open (or with a `key`) to reset the selection.
 */
export function GenerateConfigModal({ isOpen, onClose, domains, onGenerate }: GenerateConfigModalProps) {
  const [domainId, setDomainId] = useState('');
  const generate = useAsyncAction(onGenerate, { errorMessage: 'Failed to generate config' });
  const isGenerating = generate.isPending();

  const handleGenerate = async () => {
    if (!domainId) return;
    const result = await generate.run(domainId);
    if (result.ok) onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Generate NGINX Configuration</ModalHeader>
      <ModalBody>
        <p className="nginx-modal-description">
          Select a domain to generate an NGINX configuration.
          The domain must be assigned to a node.
        </p>

        <div className="nginx-modal-field">
          <label htmlFor="generate-domain-select" className="nginx-modal-label">
            Domain *
          </label>
          <Select
            id="generate-domain-select"
            value={domainId}
            onChange={setDomainId}
            placeholder="Select a domain..."
            options={domains.map((domain) => ({
              value: domain.id,
              label: `${domain.name} (${pluralize(domain.node_ids.length, 'node')})`,
            }))}
            className="nginx-modal-select"
            required
          />
          {domains.length === 0 && (
            <p className="nginx-modal-hint nginx-modal-hint-warning">
              No eligible domains found. Domains must be assigned to a node.
            </p>
          )}
        </div>

        {generate.error && (
          <div className="modal-error" role="alert">
            {generate.error}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={isGenerating}>
          Cancel
        </Button>
        <Button onClick={handleGenerate} isLoading={isGenerating} disabled={!domainId || isGenerating}>
          Generate
        </Button>
      </ModalFooter>
    </Modal>
  );
}
